"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isAppError = exports.AuthError = exports.extendEntity = exports.defineView = exports.defineEntity = exports.defineApp = void 0;
function defineApp(appDef) {
    return appDef;
}
exports.defineApp = defineApp;
/**
 * A function which returns entity definition. If given `dataSchema`, it runs validation for data when entity's
 * `onAuthPost` callback is triggered.
 * If entity has `onAnonymousAuthPostCreate` callback implemented it will validate data there as well, when triggered.
 * If entity is extended, validation will run in order in which entities were added, finishing up with the main entity.
 *
 * @param entityDef - your entity definition
 * @param dataSchema - Zod schema used for data validation. Provide a structure and data types for entity data.
 *
 * @example
 * const accountDataSchema = z.object({
 *     firstName: z
 *          .string()
 *          .min(x, { message: 'First name cannot be shorter than [x]' })
 *          .max(y, { message: 'Last name cannot be longer than [y] }),
 *     email: z.string().email({ message: 'Invalid email' }),
 *     systemStatus: z.custom<keyof typeof systemStatuses>().refine((value) => {
 *         // Note: for custom types validation, use `<schema>.refine()` method
 *         // It infers correct `value` type as opposed to passing a validation callback to custom<T>() directly, like:
 *         // custom<T>((value) => {...}), here `value` type is unknown.
 *         return !!systemStatuses[value];
 *     }, { message: `System status ${value} does not exist` }),
 * });
 *
 * export type AccountData = z.infer<typeof accountDataSchema>;
 * const accountEntity: AccountEntityDef = defineEntity(
 *     { version: 1, onMigrate: () => {}, ... },
 *     accountDataSchema
 * );
 */
function defineEntity(entityDef, dataSchema) {
    if (dataSchema) {
        const validatedDef = Object.assign(Object.assign({}, entityDef), { onAuthPost: (appState, userState, params, context) => {
                var _a;
                if (params.action === 'create' || params.action === 'update') {
                    const validationResult = dataSchema.safeParse(params.data, {});
                    if (!validationResult.success) {
                        throw new Error(validationResult.error.issues.map((issue) => `${issue.path.at(-1)}: ${issue.message}`).join('; '));
                    }
                }
                return (_a = entityDef.onAuthPost) === null || _a === void 0 ? void 0 : _a.call(entityDef, appState, userState, params, context);
            } });
        if (entityDef.onAnonymousAuthPostCreate) {
            validatedDef.onAnonymousAuthPostCreate = (appState, params, validationContext) => {
                var _a;
                const validationResult = dataSchema.safeParse(params.data);
                if (!validationResult.success) {
                    throw new Error(validationResult.error.issues.map((issue) => `${issue.path.at(-1)}: ${issue.message}`).join('; '));
                }
                return (_a = entityDef.onAnonymousAuthPostCreate) === null || _a === void 0 ? void 0 : _a.call(entityDef, appState, params, validationContext);
            };
        }
        return validatedDef;
    }
    else {
        return entityDef;
    }
}
exports.defineEntity = defineEntity;
function defineView(viewDef) {
    return viewDef;
}
exports.defineView = defineView;
/**
 * Function to extend entity by including other entities.
 * Entity columns will be merged using simple spread.
 * Callbacks will be chained together in following order:
 * onTransform -> [includedEntities].onTransform -> mainEntity.onTransform
 * First we call included entities onTransform in order that they were included
 * Lastly we call mainEntity.onTransform.
 *
 * In case of duplicate columns there is no error thrown, mainEntity will win over
 *
 * @param mainEntity - entity to be extended
 * @example
 * const account = defineEntity({
 *     version: 2,
 *     columns: { firstName: { type: 'string' } },
 *     onMigrate: (params) => {
 *         const migratedData = params.oldData;
 *         // merge function will ensure that correct versions are passed
 *         if (params.oldVersions.includes(1) && params.newVersion === 2) {
 *             // migration
 *         }
 *         return migratedData;
 *     },
 * });
 * const address = defineEntity({
 *     version: 3,
 *     columns: { street: { type: 'string' } },
 *     onMigrate: (params) => {
 *         const migratedData = params.oldData;
 *         // merge function will ensure that correct versions are passed
 *         if (params.oldVersions.includes(2) && params.newVersion === 3) {
 *             // migration
 *         }
 *         return migratedData;
 *     },
 * });
 * const finalAccount = extendEntity(account).include('address', address).build();
 */
function extendEntity(mainEntity) {
    const entities = [];
    const builder = {
        include: (name, subEntity) => {
            entities.push([name, subEntity]);
            return builder;
        },
        build: () => mergeEntities(mainEntity, entities),
    };
    return builder;
}
exports.extendEntity = extendEntity;
class AuthError extends Error {
    constructor(message) {
        super(message);
        this.type = 'auth';
    }
}
exports.AuthError = AuthError;
/**
 * Checks if the value provided is of {@link AppError} type.
 * Example usage:
 *
 * @example
 *   try {
 *     ...
 *   } catch (e) {
 *     if (isAppError(e)) {
 *       const errorType = e.type;
 *       const statusCode = e.statusCode;
 *       const errorData = e.data;
 *     }
 *   }
 *
 * @param error - value to check type
 */
function isAppError(error) {
    return typeof error === 'object' && error != null && 'message' in error && 'name' in error && 'type' in error && 'statusCode' in error;
}
exports.isAppError = isAppError;
function mergeEntities(mainEntity, namedEntities) {
    var _a, _b, _c, _d, _e;
    const allEntitiesMainLast = [...namedEntities.map(([, entityDef]) => entityDef), mainEntity];
    let versionMap = Object.assign({}, mainEntity.includedEntityVersionMap);
    let columns = {};
    const allMigrations = (_a = mainEntity.migrations) !== null && _a !== void 0 ? _a : {};
    for (const [includedEntityName, entityDef] of namedEntities) {
        if (Object.keys(versionMap).includes(includedEntityName)) {
            throw new Error('Tried to merge entity with same name: ' + includedEntityName);
        }
        versionMap = Object.assign(Object.assign({}, versionMap), entityDef.includedEntityVersionMap);
        versionMap[includedEntityName] = entityDef.version;
        Object.entries((_b = entityDef.migrations) !== null && _b !== void 0 ? _b : {}).forEach(([migrationName, migrationFunc]) => {
            allMigrations[includedEntityName + ':' + migrationName] = migrationFunc;
        });
        columns = Object.assign(Object.assign({}, columns), entityDef.columns);
    }
    columns = Object.assign(columns, mainEntity.columns);
    const onMigrate = (params, preDeployData) => {
        const { oldIncludedEntityVersionMap } = params;
        const downStreamParams = Object.assign({}, params);
        for (const [name, entityDef] of namedEntities) {
            downStreamParams.newVersion = entityDef.version;
            if (oldIncludedEntityVersionMap[name]) {
                downStreamParams.oldVersions = Array(entityDef.version - oldIncludedEntityVersionMap[name])
                    .fill(0)
                    .map((v, i) => oldIncludedEntityVersionMap[name] + i);
            }
            else {
                downStreamParams.oldVersions = [entityDef.version];
            }
            if (entityDef.onMigrate) {
                downStreamParams.oldData = entityDef.onMigrate(downStreamParams, preDeployData);
            }
        }
        params.oldData = downStreamParams.oldData;
        if (mainEntity.onMigrate) {
            return mainEntity.onMigrate(params, preDeployData);
        }
        return params.oldData;
    };
    const mergedEntity = {
        version: mainEntity.version,
        includedEntityVersionMap: versionMap,
        extendedVersion: mainEntity.extendedVersion,
        columns: columns,
        migrations: Object.keys(allMigrations).length > 0 ? allMigrations : undefined,
        onMigrate: Object.keys(allMigrations).length === 0 ? onMigrate : undefined,
    };
    if (allEntitiesMainLast.some((def) => !!def.onTransformColumns || !!def.onTransform)) {
        mergedEntity.onTransform = (context, params) => {
            var _a, _b, _c;
            let data = Object.assign({}, params.data);
            let columns = {};
            for (const entity of allEntitiesMainLast) {
                if (entity.onTransform) {
                    const dataKeysBeforeOnTransform = Object.keys((_a = params.data) !== null && _a !== void 0 ? _a : {});
                    const res = entity.onTransform(context, params);
                    const dataKeysAfterOnTransform = Object.keys((_b = params.data) !== null && _b !== void 0 ? _b : {});
                    data = Object.assign(Object.assign({}, data), ((_c = res.data) !== null && _c !== void 0 ? _c : {}));
                    for (const key of dataKeysBeforeOnTransform) {
                        if (!dataKeysAfterOnTransform.includes(key)) {
                            delete data[key];
                        }
                    }
                    columns = Object.assign(Object.assign({}, columns), res.columns);
                }
                if (entity.onTransformColumns) {
                    columns = Object.assign(Object.assign({}, columns), entity.onTransformColumns(context, params));
                }
            }
            return {
                data,
                columns,
            };
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onMassUpdateTransform)) {
        mergedEntity.onMassUpdateTransform = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onMassUpdateTransform));
    }
    if (allEntitiesMainLast.some((def) => !!def.onChange)) {
        mergedEntity.onChange = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onChange));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAfterPost)) {
        mergedEntity.onAfterPost = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAfterPost));
    }
    if (allEntitiesMainLast.some((def) => !!def.onChangeSync)) {
        mergedEntity.onChangeSync = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onChangeSync));
    }
    if (allEntitiesMainLast.some((def) => !!def.onScheduleChangedAsync)) {
        mergedEntity.onScheduleChangedAsync = (context, params) => {
            let finalResult = false;
            for (const entity of allEntitiesMainLast) {
                if (entity.onScheduleChangedAsync) {
                    const result = entity.onScheduleChangedAsync(context, params);
                    finalResult || (finalResult = result); // need to store result in variable to prevent short-circuit
                }
            }
            return finalResult;
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onChangedAsync)) {
        mergedEntity.onChangedAsync = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onChangedAsync));
    }
    if (allEntitiesMainLast.some((def) => !!def.onBeforeExport)) {
        mergedEntity.onBeforeExport = (context, exportInfo) => {
            var _a;
            const header = [];
            let preExportData = {};
            for (const entityDef of [...allEntitiesMainLast].reverse()) {
                if (entityDef.onBeforeExport) {
                    const result = entityDef.onBeforeExport(context, exportInfo);
                    if (typeof result === 'object') {
                        if (((_a = result.header) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                            header.push(...result.header);
                        }
                        if (result.preExportData) {
                            preExportData = Object.assign(preExportData, result.preExportData);
                        }
                    }
                }
            }
            if (header.length > 0 || Object.keys(preExportData).length > 0) {
                return {
                    header,
                    preExportData,
                };
            }
            return undefined;
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onExportRow)) {
        mergedEntity.onExportRow = (row, exportInfo, preExportData) => {
            const rows = [];
            const finalRow = [];
            for (const entityDef of [...allEntitiesMainLast].reverse()) {
                if (entityDef.onExportRow) {
                    const result = entityDef.onExportRow(row, exportInfo, preExportData);
                    if (result && Array.isArray(result)) {
                        if (rows.length === 0) {
                            result.forEach((row) => rows.push(row.row));
                        }
                        else {
                            result.forEach((row, i) => rows[i].push(...row.row));
                        }
                    }
                    else if (result && result.row) {
                        finalRow.push(...result.row);
                    }
                    else if (!result) {
                        // one of the onMigrate indicated that we should skip such row, honor that
                        return null;
                    }
                }
            }
            if (finalRow.length > 0) {
                return { row: finalRow };
            }
            if (rows) {
                return rows.map((row) => ({ row: row }));
            }
            return null;
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onAfterExport)) {
        mergedEntity.onAfterExport = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAfterExport));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthGet)) {
        mergedEntity.onAuthGet = wrapAuthFunctionsIntoOneWithContext(allEntitiesMainLast.map((entityDef) => entityDef.onAuthGet));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthHistoryGet)) {
        mergedEntity.onAuthHistoryGet = wrapAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAuthHistoryGet));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthList)) {
        mergedEntity.onAuthList = wrapAuthFunctionsIntoOneWithContext(allEntitiesMainLast.map((entityDef) => entityDef.onAuthList));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthPost)) {
        mergedEntity.onAuthPost = (appState, userState, params, context) => {
            let modifiedData = {};
            const mergeData = params.action === 'create' || params.action === 'update';
            allEntitiesMainLast
                .filter((def) => !!def.onAuthPost)
                .forEach((def) => {
                var _a;
                const dataKeysBeforeOnAuthPost = mergeData ? Object.keys(params.data) : [];
                const authData = (_a = def.onAuthPost) === null || _a === void 0 ? void 0 : _a.call(def, appState, userState, params, context);
                if (authData && mergeData) {
                    const dataKeysAfterOnAuthPost = Object.keys(params.data);
                    modifiedData = Object.assign(Object.assign({}, modifiedData), authData);
                    for (const key of dataKeysBeforeOnAuthPost) {
                        if (!dataKeysAfterOnAuthPost.includes(key)) {
                            delete modifiedData[key];
                        }
                    }
                }
            });
            return Object.keys(modifiedData).length > 0 ? modifiedData : undefined;
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthMassCreate)) {
        mergedEntity.onAuthMassCreate = wrapMassAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAuthMassCreate));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthMassUpdate)) {
        mergedEntity.onAuthMassUpdate = wrapMassAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAuthMassUpdate));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthMassDelete)) {
        mergedEntity.onAuthMassDelete = wrapMassAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAuthMassDelete));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthExport)) {
        mergedEntity.onAuthExport = wrapAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAuthExport));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthAttachment)) {
        mergedEntity.onAuthAttachment = wrapAuthFunctionsIntoOneWithContext(allEntitiesMainLast.map((entityDef) => entityDef.onAuthAttachment));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAttachmentChange)) {
        mergedEntity.onAttachmentChange = combineFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAttachmentChange));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAnonymousAuthAttachmentGet)) {
        mergedEntity.onAnonymousAuthAttachmentGet = wrapAnonymousAuthFunctionsIntoOneWithContext(allEntitiesMainLast.map((entityDef) => entityDef.onAnonymousAuthAttachmentGet));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAnonymousAuthGet)) {
        mergedEntity.onAnonymousAuthGet = wrapAnonymousAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAnonymousAuthGet));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAnonymousAuthList)) {
        wrapAnonymousAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAnonymousAuthList));
        mergedEntity.onAnonymousAuthList = wrapAnonymousAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAnonymousAuthList));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAnonymousAuthListParents)) {
        mergedEntity.onAnonymousAuthListParents = wrapAnonymousAuthFunctionsIntoOne(allEntitiesMainLast.map((entityDef) => entityDef.onAnonymousAuthListParents));
    }
    if (allEntitiesMainLast.some((def) => !!def.onAnonymousAuthPostCreate)) {
        mergedEntity.onAnonymousAuthPostCreate = (appState, params, validationContext) => {
            namedEntities
                .map(([, def]) => def)
                .filter((def) => !!def.onAnonymousAuthPostCreate)
                .forEach((def) => def.onAnonymousAuthPostCreate && def.onAnonymousAuthPostCreate(appState, params, validationContext));
            if (mainEntity.onAnonymousAuthPostCreate) {
                return mainEntity.onAnonymousAuthPostCreate(appState, params, validationContext);
            }
        };
    }
    if (allEntitiesMainLast.some((def) => !!def.onAuthDynamicColumn)) {
        mergedEntity.onAuthDynamicColumn = (appState, userState, params) => {
            namedEntities
                .map(([, def]) => def)
                .filter((def) => !!def.onAuthDynamicColumn)
                .forEach((def) => def.onAuthDynamicColumn && def.onAuthDynamicColumn(appState, userState, params));
            if (mainEntity.onAuthDynamicColumn) {
                return mainEntity.onAuthDynamicColumn(appState, userState, params);
            }
        };
    }
    const entitiesWithIdentityHeader = allEntitiesMainLast.filter((def) => !!def.identityHeader);
    const entitiesWithOnBeforeImport = allEntitiesMainLast.filter((def) => !!def.onBeforeImport);
    const entitiesWithOnImportRow = allEntitiesMainLast.filter((def) => !!def.onImportRow);
    const entitiesWithOnAuthImport = allEntitiesMainLast.filter((def) => !!def.onAuthImport);
    const entitiesWithOnAfterImport = allEntitiesMainLast.filter((def) => !!def.onAfterImport);
    const entitiesWithAuditColumns = allEntitiesMainLast.filter((def) => { var _a; return !!((_a = def.audit) === null || _a === void 0 ? void 0 : _a.columnForAuditIdReplacement); });
    if (entitiesWithAuditColumns.length > 1) {
        throw Error('extendEntity does not support columnForAuditIdReplacement merge, define columnForAuditIdReplacement where audit object defined');
    }
    if (entitiesWithAuditColumns.length === 1) {
        (_c = mergedEntity.audit) !== null && _c !== void 0 ? _c : (mergedEntity.audit = {});
        mergedEntity.audit.columnForAuditIdReplacement = (_d = entitiesWithAuditColumns[0].audit) === null || _d === void 0 ? void 0 : _d.columnForAuditIdReplacement;
    }
    if (entitiesWithIdentityHeader.length > 1) {
        throw Error('extendEntity does not support identityHeader merge, define identityHeader where onImportRow callback defined');
    }
    if (entitiesWithIdentityHeader.length === 1) {
        mergedEntity.identityHeader = entitiesWithIdentityHeader[0].identityHeader;
    }
    if (entitiesWithOnImportRow.length > 1) {
        throw Error('extendEntity does not support onImportRow merge');
    }
    if (entitiesWithOnAuthImport.length > 1) {
        throw Error('extendEntity does not support onAuthImport merge');
    }
    if (entitiesWithOnAfterImport.length > 1) {
        throw Error('extendEntity does not support onAfterImport merge');
    }
    if (entitiesWithOnBeforeImport.length > 1) {
        throw Error('extendEntity does not support onBeforeImport merge');
    }
    if (entitiesWithOnImportRow.length === 1) {
        const { onBeforeImport, onImportRow, onAuthImport, onAfterImport } = entitiesWithOnImportRow[0];
        mergedEntity.onBeforeImport = onBeforeImport;
        mergedEntity.onImportRow = onImportRow;
        mergedEntity.onAuthImport = onAuthImport;
        mergedEntity.onAfterImport = onAfterImport;
    }
    if (allEntitiesMainLast.some((def) => { var _a; return (_a = def.audit) === null || _a === void 0 ? void 0 : _a.storageReads; })) {
        (_e = mergedEntity.audit) !== null && _e !== void 0 ? _e : (mergedEntity.audit = {});
        mergedEntity.audit.storageReads = true;
    }
    return mergedEntity;
}
function combineFunctionsIntoOne(functions) {
    return (...args) => {
        const result = functions.filter((fn) => !!fn).reduce((result, fn) => Object.assign(result, fn(...args)), {});
        if (Object.keys(result).length === 0) {
            return undefined;
        }
        return result;
    };
}
function wrapAuthFunctionsIntoOne(authFunctions) {
    return (appState, userState, authParams) => {
        let resp = authParams;
        for (const fn of authFunctions) {
            if (fn) {
                resp = Object.assign(resp, fn(appState, userState, resp));
            }
        }
        return resp;
    };
}
function wrapAuthFunctionsIntoOneWithContext(authFunctions) {
    return (appState, userState, authParams, context) => {
        let resp = authParams;
        for (const fn of authFunctions) {
            if (fn) {
                resp = Object.assign(resp, fn(appState, userState, resp, context));
            }
        }
        return resp;
    };
}
function wrapAnonymousAuthFunctionsIntoOne(authFunctions) {
    return (appState, authParams) => {
        let resp = authParams;
        for (const fn of authFunctions) {
            if (fn) {
                resp = Object.assign(resp, fn(appState, resp));
            }
        }
        return resp;
    };
}
function wrapMassAuthFunctionsIntoOne(authFunctions) {
    return (appState, userState, authParams, context) => {
        let resp = authParams;
        for (const fn of authFunctions) {
            if (fn) {
                resp = Object.assign(resp, fn(appState, userState, resp, context));
            }
        }
        return resp;
    };
}
function wrapAnonymousAuthFunctionsIntoOneWithContext(authFunctions) {
    return (appState, authParams, context) => {
        let resp = authParams;
        for (const fn of authFunctions) {
            if (fn) {
                resp = Object.assign(resp, fn(appState, resp, context));
            }
        }
        return resp;
    };
}
